/**
 * Storage for the "Diesem Browser vertrauen" token.
 *
 * The token lets the backend skip the second factor for this browser until it expires.
 * It is stored per e-mail address so that two people sharing a machine do not overwrite
 * each other's token – the backend binds each token to one user anyway, an overwritten
 * one would simply be rejected and cost the other person an extra challenge.
 *
 * It lives in localStorage next to the JWT. That is deliberate: it is not a credential on
 * its own, since it only ever takes effect together with a correct password.
 */
const PREFIX = 'elga_trusted_device'

/**
 * vue-auth hands the login response body to nobody: it resolves with the /auth/me payload
 * instead. The axios interceptor in main.js therefore parks a freshly issued token here,
 * and the login page – which is the one place that knows the e-mail address – claims it.
 */
let pendingToken = null

function keyFor(email) {
  return `${PREFIX}:${(email || '').trim().toLowerCase()}`
}

export default {
  stashPending(token) {
    pendingToken = token || null
  },

  /** Called before every login attempt: a token parked by an attempt that never reached
   * `commitPending` must not end up under the next address. */
  discardPending() {
    pendingToken = null
  },

  /** Stores a token parked by the interceptor, if there is one. */
  commitPending(email) {
    if (pendingToken && email) {
      this.set(email, pendingToken)
    }
    pendingToken = null
  },

  get(email) {
    if (!email) {
      return null
    }
    try {
      return window.localStorage.getItem(keyFor(email))
    } catch (error) {
      // Private mode or disabled storage: fall back to challenging every time.
      return null
    }
  },

  set(email, token) {
    if (!email || !token) {
      return
    }
    try {
      window.localStorage.setItem(keyFor(email), token)
    } catch (error) {
      // Not being able to remember the browser is an inconvenience, not an error.
    }
  },

  clear(email) {
    if (!email) {
      return
    }
    try {
      window.localStorage.removeItem(keyFor(email))
    } catch (error) {
      // nothing to do
    }
  }
}
